import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Modal, Carousel, Empty, Spin, message, notification } from 'antd';
import { PlayCircleOutlined, UserOutlined } from '@ant-design/icons';
import { getActivePartnerNews, incrementNewsView } from '../../API/services/partnersNewsService';
import { getProfiles } from '../../API/services/alkymersService';
import { HiredStatus } from '../../constants/enums';
import moment from 'moment';
import { t } from '../../i18n/i18n';
import './NewsSection.scss';

const NewsSection = () => {
  const history = useHistory();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalContentLoading, setModalContentLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const carouselRef = useRef(null);
  const videoRef = useRef(null);
  const imageRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    fetchNews();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isMountedRef.current) {
        setWindowWidth(window.innerWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchNews = async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day').toISOString();

      const profilesResponse = await getProfiles(
        {
          limit: '1',
          minChallengeDate: thirtyDaysAgo,
        },
        true
      );

      if (!isMountedRef.current) return;

      const newTalentsCount = profilesResponse?.pagination?.total || 0;

      const marketplaceNews = {
        id: 'marketplace-fixed',
        type: 'marketplace',
        title: t('NEWS_SECTION_MARKETPLACE_TITLE'),
        description: `${newTalentsCount} ${t('NEWS_SECTION_NEW_TALENTS')}`,
        thumbnail: null,
        icon: <UserOutlined />,
        onClick: () => history.push('/profiles'),
        isFixed: true,
      };

      const cmsNews = await getActivePartnerNews(9);

      if (!isMountedRef.current) return;

      const mappedNews = cmsNews.map((item) => ({
        id: item.id,
        type: item.type,
        title: item.title,
        description: item.description,
        thumbnail: item.imageUrl,
        videoUrl: item.videoUrl,
        textContent: item.textContent,
        linkUrl: item.linkUrl,
        linkText: item.linkText,
      }));

      setNewsItems([marketplaceNews, ...mappedNews]);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error loading news:', error);
      notification.error({
        message: t('NEWS_SECTION_ERROR_LOADING'),
        placement: 'topRight',
      });
      setNewsItems([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleItemClick = async (item) => {
    if (item.isFixed) {
      item.onClick();
      return;
    }

    if (item.id && typeof item.id === 'number') {
      incrementNewsView(item.id).catch(console.error);
    }

    if (item.type === 'video' || item.type === 'image') {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      setModalContentLoading(true);
      setSelectedItem(item);
      setModalVisible(true);
    } else if (item.type === 'link') {
      window.open(item.linkUrl, '_blank', 'noopener,noreferrer');
    } else if (item.type === 'text') {
      setSelectedItem(item);
      setModalVisible(true);
    }
  };

  const handleCarouselChange = (current) => {
    setCurrentSlide(current);
  };

  const goToSlide = (index) => {
    carouselRef.current?.goTo(index);
  };

  const handleModalClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setModalVisible(false);
    setSelectedItem(null);
    setModalContentLoading(false);
  };

  const handleVideoLoad = () => {
    if (isMountedRef.current) {
      setModalContentLoading(false);
    }
  };

  const handleImageLoad = () => {
    if (isMountedRef.current) {
      setModalContentLoading(false);
    }
  };

  if (loading) {
    return (
      <Card title={t('NEWS_SECTION_TITLE')} className="news-card" bordered={false}>
        <div className="news-loading">
          <Spin />
        </div>
      </Card>
    );
  }

  if (newsItems.length === 0) {
    return (
      <Card title={t('NEWS_SECTION_TITLE')} className="news-card" bordered={false}>
        <Empty description={t('NEWS_SECTION_EMPTY')} />
      </Card>
    );
  }

  const getSlidesToShow = () => {
    if (windowWidth <= 768) return 1;
    if (windowWidth <= 1024) return 2;
    return 3;
  };

  const slidesToShow = getSlidesToShow();
  const needsArrows = newsItems.length > slidesToShow;
  const enableInfiniteScroll = newsItems.length > slidesToShow;

  return (
    <>
      <Card id="NEWS_SECTION" title={t('NEWS_SECTION_TITLE')} className="news-card" bordered={false}>
        <Carousel
          ref={carouselRef}
          autoplay={enableInfiniteScroll}
          autoplaySpeed={5000}
          arrows={needsArrows}
          dots={false}
          infinite={enableInfiniteScroll}
          slidesToShow={3}
          slidesToScroll={1}
          className="news-carousel"
          afterChange={handleCarouselChange}
          draggable={false}
          swipe={false}
          responsive={[
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: 768,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
              },
            },
          ]}
        >
          {newsItems.map((item) => (
            <div key={item.id} className="news-slide">
              <div className="news-item" onClick={() => handleItemClick(item)}>
                <div className="news-thumbnail">
                  {item.thumbnail ? (
                    <img src={item.thumbnail} alt={item.title} />
                  ) : item.icon ? (
                    <div className="news-icon-container">{item.icon}</div>
                  ) : null}
                  {item.type === 'video' && (
                    <div className="play-overlay">
                      <PlayCircleOutlined />
                    </div>
                  )}
                  {item.isFixed && (
                    <div className="fixed-badge">{t('NEWS_SECTION_FEATURED_BADGE')}</div>
                  )}
                </div>
                <div className="news-content">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      </Card>

      <Modal
        title={selectedItem?.title}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        className="news-modal"
        maskClosable={true}
        destroyOnClose={true}
      >
        {selectedItem?.type === 'video' && (
          <>
            {modalContentLoading && (
              <div className="news-modal-loading">
                <Spin size="large" />
              </div>
            )}
            <video
              ref={videoRef}
              controls
              width="100%"
              autoPlay
              src={selectedItem.videoUrl}
              onLoadedData={handleVideoLoad}
              onCanPlay={handleVideoLoad}
              onError={handleVideoLoad}
              style={{ display: modalContentLoading ? 'none' : 'block' }}
            >
              {t('NEWS_SECTION_VIDEO_NOT_SUPPORTED')}
            </video>
          </>
        )}
        {selectedItem?.type === 'image' && (
          <>
            {modalContentLoading && (
              <div className="news-modal-loading">
                <Spin size="large" />
              </div>
            )}
            <img
              ref={imageRef}
              src={selectedItem.thumbnail}
              alt={selectedItem.title}
              style={{
                width: '100%',
                height: 'auto',
                display: modalContentLoading ? 'none' : 'block',
              }}
              onLoad={handleImageLoad}
              onError={handleImageLoad}
            />
          </>
        )}
        {selectedItem?.type === 'text' && (
          <div
            className="text-content"
            dangerouslySetInnerHTML={{ __html: selectedItem.textContent }}
          />
        )}
        <div className="news-modal-description">
          <p>{selectedItem?.description}</p>
          {selectedItem?.linkUrl && (
            <a href={selectedItem.linkUrl} target="_blank" rel="noopener noreferrer">
              {selectedItem.linkText || t('NEWS_SECTION_SEE_MORE')}
            </a>
          )}
        </div>
      </Modal>
    </>
  );
};

export default NewsSection;
